import Markdown from 'markdown-to-jsx';
import { useLayoutEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import Icon from 'components/icons/Icon';
import { IconSymbol } from 'components/icons/Icon.types';

import './MarkdownContent.scss';

type MarkdownContentProps = {
  content?: string | (() => string);
  children?: string;
  escapedLineBreaks?: boolean;
  className?: string;
  noLinks?: boolean;
  collapsible?: boolean;
  maxLines?: number;
};

export function MarkdownContent(props: MarkdownContentProps) {
  const {
    content,
    children,
    className,
    escapedLineBreaks,
    noLinks,
    collapsible = false,
    maxLines = 10,
  } = props;
  const [processedContent, setProcessedContent] = useState<string>('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false); // New state
  const contentRef = useRef<HTMLDivElement>(null); // New ref

  useLayoutEffect(() => {
    const textContent =
      children ?? (typeof content === 'function' ? content() : content);

    if (textContent) {
      setProcessedContent(preprocess(textContent, escapedLineBreaks));
    }
  }, [content, children, escapedLineBreaks]);

  // Check for overflow after content renders
  useLayoutEffect(() => {
    if (collapsible && contentRef.current && !isExpanded) {
      const isContentOverflowing =
        contentRef.current.scrollHeight > contentRef.current.clientHeight;
      setIsOverflowing(isContentOverflowing);
    } else {
      setIsOverflowing(false);
    }
  }, [processedContent, collapsible, isExpanded]);

  function preprocess(c: string, escapedLineBreaks?: boolean): string {
    let result = c.replace(/(?<!\n)\n(?!\n)/g, '  \n');
    if (escapedLineBreaks) {
      result = result.replace(/\\n/g, '  \n');
    }
    return result;
  }

  const maxHeight =
    collapsible && !isExpanded ? `calc(${maxLines} * 1.5em)` : 'none';

  return (
    <>
      <div>
        <div
          ref={contentRef}
          className={className}
          style={{
            maxHeight,
            overflow: collapsible && !isExpanded ? 'auto' : 'visible',
            transition: 'max-height 0.3s ease-in-out',
          }}
        >
          <Markdown
            options={{
              forceBlock: true,
              overrides: {
                a: noLinks
                  ? {
                      component: (p: any) => <span>{p.children}</span>,
                    }
                  : {
                      props: {
                        target: '_blank',
                        rel: 'noopener noreferrer',
                      },
                    },
              },
            }}
          >
            {processedContent}
          </Markdown>
        </div>
      </div>
      {collapsible && (isExpanded || isOverflowing) && (
        <div
          className="markdown_toggle"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Icon
            symbol={isExpanded ? IconSymbol.up : IconSymbol.down}
            size={24}
          />
          <FormattedMessage
            id={isExpanded ? 'GENERAL.SHOW_LESS' : 'GENERAL.SHOW_MORE'}
          />
        </div>
      )}
    </>
  );
}

export function MarkdownExplainer() {
  return (
    <div className="markdown_explainer">
      <FormattedMessage id="GENERAL.MARKDOWN.MD_EXPLAINER" />
      <br />
      <FormattedMessage id="GENERAL.MARKDOWN.MD_EXPLAINER_2" />
      <br />
      <a href="https://www.markdownguide.org/basic-syntax/" target="_blank">
        <FormattedMessage id="GENERAL.MARKDOWN.MD_LINK" />
        <Icon symbol={IconSymbol.newWindow} size={12} />
      </a>
    </div>
  );
}
